import { DEV } from 'cc/env';
import {
    _decorator,
    Component,
    NodeEventType,
    v3,
    game,
    Node,
    Widget,
    Enum,
    warn,
    director,
    Director,
    Prefab,
    instantiate,
    EventTouch,
    Canvas,
    EventMouse,
} from 'cc';

const { ccclass, property, requireComponent } = _decorator;

// system cursor style
export enum ECursorStyle {
    url = 'url',
    default = 'default',
    auto = 'auto',
    crosshair = 'crosshair',
    pointer = 'pointer',
    move = 'move',
    e_resize = 'e-resize',
    ne_resize = 'ne-resize',
    nw_resize = 'nw-resize',
    n_resize = 'n-resize',
    se_resize = 'se-resize',
    sw_resize = 'sw-resize',
    s_resize = 's-resize',
    w_resize = 'w-resize',
    text = 'text',
    wait = 'wait',
    help = 'help',
    none = 'none',
}

@ccclass('CustomCursorProperties')
export class CustomCursorProperties {
    @property({ tooltip: DEV && '游標名稱' })
    public cursorName: string = '';
    @property({ tooltip: DEV && '是否為系統游標' })
    public isSystemCursor: boolean = false;
    @property({
        type: Prefab,
        tooltip: DEV && '游標Prefab',
        visible(this: CustomCursorProperties) {
            return !this.isSystemCursor;
        },
    })
    public customCursorPrefab: Prefab = null;
    @property({
        type: Enum(ECursorStyle),
        tooltip: DEV && '系統游標樣式',
        visible(this: CustomCursorProperties) {
            return this.isSystemCursor;
        },
    })
    public systemCursorStyle: ECursorStyle = ECursorStyle.default;
}

@ccclass('CursorCustomizer')
@requireComponent(Widget)
export default class CursorCustomizer extends Component {
    private static _lastMousePosition = null;
    private static _lastCursorTypeName: string = '';

    @property({
        type: [CustomCursorProperties],
        tooltip: DEV && '游標圖片設定',
    })
    protected customCursorProperties: CustomCursorProperties[] = [];

    @property({
        tooltip: DEV && '載入時啟用的自定義游標',
    })
    protected customCursorOnLoad: string = '';

    @property(Canvas)
    protected canvas: Canvas = null;

    private _cursorPropertiesMap: Map<string, CustomCursorProperties> = new Map();
    private _fakeCursorNode: Node = null;

    /**
     * set cursor style by cursor type name
     * @param cursorTypeName cursor type name
     */
    public setCursorStyle(cursorTypeName: string) {
        if (this._cursorPropertiesMap.has(cursorTypeName)) {
            const cursorProperty = this._cursorPropertiesMap.get(cursorTypeName);
            if (cursorProperty.isSystemCursor) {
                this.setECursorStyle(cursorProperty.systemCursorStyle);
                this._destroyCustomCursor();
            } else {
                this.setCustomCursorByPrefab(cursorProperty.customCursorPrefab);
                this.setECursorStyle(ECursorStyle.none);
            }
            CursorCustomizer._lastCursorTypeName = cursorTypeName;
        } else {
            warn(`cursor type ${cursorTypeName} not found`);
        }
    }

    protected onLoad() {
        this.customCursorProperties.forEach((properties) => {
            this._cursorPropertiesMap.set(properties.cursorName, properties);
        });

        director.on(Director.EVENT_AFTER_SCENE_LAUNCH, this._recoverLastCursorSettings, this);
        this.on(NodeEventType.MOUSE_MOVE, this._onMouseMove, this);
        this.on(NodeEventType.MOUSE_ENTER, this.enableFakeCursor, this);
        const canvas = this.canvas || this.node.scene.getComponentInChildren(Canvas);
        canvas.on(NodeEventType.TOUCH_MOVE, this._onTouchEvent, this);
        canvas.on(
            NodeEventType.TOUCH_START,
            (event: EventTouch) => {
                this.enableFakeCursor();
                this._onTouchEvent(event);
            },
            this,
        );
        this.node.zIndex = Number.MAX_SAFE_INTEGER;
        this._setCustomCursorOnLoad();
    }

    /**
     * set custom cursor style by cursor type name
     * @param cursorSpriteFrame set custom cursor style by cursor type name
     */
    protected setCustomCursorByPrefab(cursorPrefab: Prefab) {
        if (!cursorPrefab) {
            warn('cursor prefab is null');
            return;
        }

        this._instantiationCustomCursor(cursorPrefab);
    }

    /**
     * set system cursor style
     * @param style system cursor style
     * @param url cursor url
     */
    protected setECursorStyle(style: ECursorStyle, url = '') {
        if (style === ECursorStyle.url) {
            game.canvas.style.cursor = `url(${url}),auto`;
        } else {
            game.canvas.style.cursor = style;
        }
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _instantiationCustomCursor(cursorPrefab: Prefab) {
        // destroy old custom cursor if exist
        this._destroyCustomCursor();
        this._fakeCursorNode = instantiate(cursorPrefab);
        this._fakeCursorNode.parent = this.node;
        if (!CursorCustomizer._lastMousePosition) {
            this._fakeCursorNode.active = false;
        }
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _destroyCustomCursor() {
        if (this._fakeCursorNode) {
            this._fakeCursorNode.destroy();
            this._fakeCursorNode = null;
        }
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _recoverLastCursorSettings() {
        this.setCursorStyle(CursorCustomizer._lastCursorTypeName);
        if (this._fakeCursorNode && CursorCustomizer._lastMousePosition) {
            this._fakeCursorNode.worldPosition = CursorCustomizer._lastMousePosition;
        }
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _onMouseMove(event: EventMouse) {
        const pos = event.getUILocation();
        const worldPosition = v3(pos.x, pos.y, 0);
        if (this._fakeCursorNode) {
            this._fakeCursorNode.worldPosition = worldPosition;
        }
        // save last mouse position for scene switch
        CursorCustomizer._lastMousePosition = worldPosition;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _onTouchEvent(event: EventTouch) {
        const pos = event.getUILocation();
        const worldPosition = v3(pos.x, pos.y, 0);
        if (this._fakeCursorNode) {
            this._fakeCursorNode.worldPosition = worldPosition;
        }
        // save last mouse position for scene switch
        CursorCustomizer._lastMousePosition = worldPosition;
    }

    // set custom cursor on load: enable custom cursor and set system cursor style to none
    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _setCustomCursorOnLoad() {
        if (this.customCursorOnLoad) {
            this.setCursorStyle(this.customCursorOnLoad);
        }
    }

    protected enableFakeCursor() {
        if (this._fakeCursorNode && !CursorCustomizer._lastMousePosition) {
            this._fakeCursorNode.active = true;
        }
    }
}
